/**
 * RetrievalDiagnostics Component
 *
 * Purpose: Small diagnostics view for retrieval_log metrics per airline/fleet/stage.
 *
 * Features:
 * - Shows average max_similarity, max_reranker_score and latency per pipeline_stage and fleet.
 * - Filters by currently selected airline (via AirlineContext).
 * - Super admin–friendly; read-only view backed by RLS.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAirline } from '../../contexts/AirlineContext';
import type { FleetModel, RetrievalLog } from '../../lib/types';
import { Loader2, BarChart2, AlertTriangle } from 'lucide-react';

interface AggregatedMetrics {
  pipeline_stage: string | null;
  fleet_model_id: string | null;
  fleet_code?: string;
  fleet_name?: string;
  count: number;
  avgMaxSimilarity: number | null;
  avgMaxRerankerScore: number | null;
  avgLatencyMs: number | null;
  rerankerOnRate: number | null;
}

export const RetrievalDiagnostics: React.FC = () => {
  const { selectedAirline, isSuperAdmin } = useAirline();
  const airlineId = selectedAirline?.airline_id || null;

  const {
    data,
    isLoading,
    error,
  } = useQuery<{
    logs: RetrievalLog[];
    fleets: FleetModel[];
  }>({
    queryKey: ['retrieval-diagnostics', airlineId],
    enabled: !!airlineId && isSuperAdmin,
    queryFn: async () => {
      if (!airlineId) {
        return { logs: [], fleets: [] };
      }

      // Last 7 days diagnostics
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const [logsRes, fleetsRes] = await Promise.all([
        supabase
          .from('retrieval_log')
          .select(
            'id, org_id, session_id, user_id, query, top_k, doc_type_filter, language_filter, version_filter, chunk_ids, retrieval_time_ms, pipeline_stage, competency_code, fleet_model_id, use_reranker, k_recall, k_final, max_similarity, max_reranker_score, error_code, created_at'
          )
          .eq('org_id', airlineId)
          .gte('created_at', sevenDaysAgo.toISOString())
          .order('created_at', { ascending: false })
          .limit(500),
        supabase.from('fleet_models').select('*'),
      ]);

      if (logsRes.error) {
        throw logsRes.error;
      }
      if (fleetsRes.error) {
        throw fleetsRes.error;
      }

      return {
        logs: (logsRes.data as any[]) || [],
        fleets: (fleetsRes.data as any[]) || [],
      };
    },
  });

  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle className="mt-0.5" size={18} />
          <div>
            <p className="font-semibold">Super admin access required</p>
            <p>This diagnostics view is only available to Global (super admin) users.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!airlineId) {
    return (
      <div className="p-6">
        <p className="text-sm text-steel">
          Select an airline in the top bar to view retrieval diagnostics.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="animate-spin text-orca-elevate" size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
          <p className="font-semibold mb-1">Failed to load retrieval diagnostics</p>
          <p>{(error as any).message || String(error)}</p>
        </div>
      </div>
    );
  }

  const logs = data?.logs ?? [];
  const fleets = data?.fleets ?? [];

  const metrics: AggregatedMetrics[] = computeAggregatedMetrics(logs, fleets);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-orca-elevate/10 flex items-center justify-center">
            <BarChart2 className="text-orca-elevate" size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-navy">Retrieval Diagnostics</h1>
            <p className="text-sm text-steel">
              Last 7 days · Airline: {selectedAirline?.name} ({selectedAirline?.code})
            </p>
          </div>
        </div>
        <div className="text-xs text-steel">
          <p>Samples: {logs.length}</p>
        </div>
      </div>

      {/* No data */}
      {logs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-6 text-sm text-steel">
          No retrieval_log entries found for this airline in the last 7 days.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-4">
          <table className="w-full text-xs">
            <thead className="bg-navy text-white">
              <tr>
                <th className="px-3 py-2 text-left">Stage</th>
                <th className="px-3 py-2 text-left">Fleet</th>
                <th className="px-3 py-2 text-center">Samples</th>
                <th className="px-3 py-2 text-center">Avg Max Similarity</th>
                <th className="px-3 py-2 text-center">Avg Max Reranker Score</th>
                <th className="px-3 py-2 text-center">Avg Latency (ms)</th>
                <th className="px-3 py-2 text-center">Reranker Usage</th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((m) => (
                <tr key={`${m.pipeline_stage || 'none'}-${m.fleet_model_id || 'none'}`} className="border-b border-steel/20">
                  <td className="px-3 py-2">
                    <span className="font-medium text-navy">
                      {m.pipeline_stage || 'N/A'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {m.fleet_code ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orca-elevate/10 text-orca-elevate">
                        {m.fleet_code}
                      </span>
                    ) : (
                      <span className="text-steel/60">All Fleets</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className="font-semibold text-navy">{m.count}</span>
                  </td>
                  <td className="px-3 py-2">
                    <BarCell value={m.avgMaxSimilarity} />
                  </td>
                  <td className="px-3 py-2">
                    <BarCell value={m.avgMaxRerankerScore} color="magenta" />
                  </td>
                  <td className="px-3 py-2 text-center">
                    {m.avgLatencyMs !== null ? m.avgLatencyMs.toFixed(0) : '–'}
                  </td>
                  <td className="px-3 py-2">
                    <BarCell value={m.rerankerOnRate} color="green" suffix="%" scale={100} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Helper: compute aggregates client-side
function computeAggregatedMetrics(
  logs: RetrievalLog[],
  fleets: FleetModel[]
): AggregatedMetrics[] {
  const groups = new Map<string, AggregatedMetrics>();

  for (const log of logs) {
    const key = `${log.pipeline_stage || 'none'}|${log.fleet_model_id || 'none'}`;
    let group = groups.get(key);
    if (!group) {
      const fleet = log.fleet_model_id
        ? fleets.find((f) => f.id === log.fleet_model_id)
        : undefined;
      group = {
        pipeline_stage: (log as any).pipeline_stage ?? null,
        fleet_model_id: (log as any).fleet_model_id ?? null,
        fleet_code: fleet?.code,
        fleet_name: fleet?.name,
        count: 0,
        avgMaxSimilarity: 0,
        avgMaxRerankerScore: 0,
        avgLatencyMs: 0,
        rerankerOnRate: 0,
      };
      groups.set(key, group);
    }

    group.count += 1;

    // Accumulate sums; we'll divide later
    const maxSim = (log as any).max_similarity as number | null;
    const maxReScore = (log as any).max_reranker_score as number | null;
    const latency = log.retrieval_time_ms ?? null;
    const useReranker = (log as any).use_reranker;

    if (maxSim != null) {
      group.avgMaxSimilarity = (group.avgMaxSimilarity || 0) + maxSim;
    }
    if (maxReScore != null) {
      group.avgMaxRerankerScore = (group.avgMaxRerankerScore || 0) + maxReScore;
    }
    if (latency != null) {
      group.avgLatencyMs = (group.avgLatencyMs || 0) + latency;
    }
    if (useReranker === true) {
      group.rerankerOnRate = (group.rerankerOnRate || 0) + 1;
    }
  }

  const result: AggregatedMetrics[] = [];

  for (const group of groups.values()) {
    const count = group.count || 1;
    result.push({
      ...group,
      avgMaxSimilarity:
        group.avgMaxSimilarity !== null ? group.avgMaxSimilarity / count : null,
      avgMaxRerankerScore:
        group.avgMaxRerankerScore !== null
          ? group.avgMaxRerankerScore / count
          : null,
      avgLatencyMs:
        group.avgLatencyMs !== null ? group.avgLatencyMs / count : null,
      rerankerOnRate:
        group.rerankerOnRate !== null
          ? (group.rerankerOnRate / count) * 100
          : null,
    });
  }

  // Sort by stage, then fleet
  result.sort((a, b) => {
    if ((a.pipeline_stage || '') < (b.pipeline_stage || '')) return -1;
    if ((a.pipeline_stage || '') > (b.pipeline_stage || '')) return 1;
    return (a.fleet_code || '').localeCompare(b.fleet_code || '');
  });

  return result;
}

interface BarCellProps {
  value: number | null;
  color?: 'blue' | 'magenta' | 'green';
  suffix?: string;
  scale?: number;
}

const BarCell: React.FC<BarCellProps> = ({
  value,
  color = 'blue',
  suffix = '',
  scale = 1,
}) => {
  if (value === null || Number.isNaN(value)) {
    return <span className="text-steel/50 text-center block">–</span>;
  }

  const pct = Math.max(0, Math.min(100, value * (100 / scale)));
  const bg =
    color === 'magenta'
      ? 'bg-orca-assurance'
      : color === 'green'
      ? 'bg-green-500'
      : 'bg-orca-elevate';

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-steel/10 rounded-full overflow-hidden">
        <div
          className={`${bg} h-2 rounded-full transition-all`}
          style={{ width: `${pct.toFixed(0)}%` }}
        />
      </div>
      <span className="w-14 text-right text-[11px] text-navy font-mono">
        {scale === 1 ? value.toFixed(2) : value.toFixed(0)}
        {suffix}
      </span>
    </div>
  );
};

export default RetrievalDiagnostics;


